import { FormEvent, useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { ArrowLeft, CalendarIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format, parse } from "date-fns";
import { fr } from "date-fns/locale";
import { createPatient, createMedicalRecord, createVaccinationPro } from "../../../api/professionalPatient";
import {
  getBeninArrondissements,
  getBeninCommunes,
  getBeninDepartements,
  getProfessionsMetiers,
  type BeninArrondissement,
  type BeninCommune,
  type BeninDepartement,
  type ProfessionMetier,
} from "../../../api/publicBenin";
import { useProfessionalAuth } from "../../../store/authProfessionalStore";
import { RequiredMark } from "../../../components/RequiredMark";
import PatientAccessOtpModal from "../../../components/PatientAccessOtpModal";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Calendar } from "../../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { showToast } from "../../../components/ui/toastHotSonner";

const GROUPES_SANGUINS = ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"];
const ELECTROPHORESES = ["AA", "AS", "SS", "AC", "SC"];


function toSlug(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

type Step = 1 | 2 | 3;

/** Indicatif Bénin — les 10 chiffres saisis sont le numéro national (même découpe 5×2 que le NIP). */
const BJ_DIAL = "+229";

export function PatientCreationWizard({
  initialNip = "",
  onCreated,
}: {
  initialNip?: string;
  onCreated: (patientNip: string) => void;
}) {
  const { professional } = useProfessionalAuth();
  const proNip = professional?.nip?.trim();
  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pendingDossier, setPendingDossier] = useState<{
    patientNip: string;
    payload: Record<string, unknown>;
  } | null>(null);

  const [nip, setNip] = useState(initialNip);
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [birth, setBirth] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [numero, setNumero] = useState("");
  const [email, setEmail] = useState("");
  const [job, setJob] = useState("");
  const [jobAutre, setJobAutre] = useState("");
  const [departementId, setDepartementId] = useState("");
  const [communeNom, setCommuneNom] = useState("");
  const [arrondissementNom, setArrondissementNom] = useState("");
  const [quartierLibre, setQuartierLibre] = useState("");
  const [departements, setDepartements] = useState<BeninDepartement[]>([]);
  const [communes, setCommunes] = useState<BeninCommune[]>([]);
  const [arrondissements, setArrondissements] = useState<BeninArrondissement[]>([]);
  const [metiers, setMetiers] = useState<ProfessionMetier[]>([]);
  const [loadingMetiers, setLoadingMetiers] = useState(true);
  const [loadingDepartements, setLoadingDepartements] = useState(true);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  const [loadingArrondissements, setLoadingArrondissements] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);

  const [taille, setTaille] = useState("");
  const [poids, setPoids] = useState("");
  const [gs, setGs] = useState("");
  const [electro, setElectro] = useState("");
  const [allergies, setAllergies] = useState("");
  const [intolerances, setIntolerances] = useState("");
  /**
   * `lot`/`siteInjection` par vaccin : sans eux, VaccinationController.normalizeVaccinationCreateBody
   * (backend) retombe silencieusement sur "N/A" pour les deux — c'est exactement le bug #2
   * (point_correction.md DAY_8) : la vaccination était créée mais sans info exploitable.
   */
  type VaccinRow = { nom: string; lot: string; siteInjection: string };
  const [vaccins, setVaccins] = useState<VaccinRow[]>([
    { nom: "Polio", lot: "", siteInjection: "" },
    { nom: "Fievre jaune", lot: "", siteInjection: "" },
    { nom: "COVID-19", lot: "", siteInjection: "" },
  ]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [coverage, setCoverage] = useState<"oui" | "non" | "verification">("verification");
  const [soins, setSoins] = useState<string[]>(["consultations", "maternité", "chirurgie"]);
  const [soinModalOpen, setSoinModalOpen] = useState(false);
  const [soinDraft, setSoinDraft] = useState("");
  const soinInputRef = useRef<HTMLInputElement | null>(null);

  const phonePartRefs = useRef<Array<HTMLInputElement | null>>([]);
  const nipPartRefs = useRef<Array<HTMLInputElement | null>>([]);
  const tailleRef = useRef<HTMLInputElement | null>(null);
  const poidsRef = useRef<HTMLInputElement | null>(null);

  const applyDigitsWithCaret = (
    event: React.ChangeEvent<HTMLInputElement>,
    setter: (value: string) => void,
    ref: React.RefObject<HTMLInputElement | null>,
    maxLength: number
  ) => {
    const input = event.target;
    const selection = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = (input.value.slice(0, selection).match(/\d/g) || []).length;
    const only = input.value.replace(/\D/g, "").slice(0, maxLength);
    setter(only);
    requestAnimationFrame(() => {
      if (ref.current) {
        const position = Math.min(digitsBeforeCaret, only.length);
        try {
          ref.current.focus();
          ref.current.setSelectionRange(position, position);
        } catch {
          /* ignore */
        }
      }
    });
  };

const emailValid = useMemo(() => /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email.trim()), [email]);
  const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/;
  const nomValid = useMemo(() => nom.trim().length > 0 && nameRegex.test(nom.trim()), [nom]);
  const prenomValid = useMemo(() => prenom.trim().length > 0 && nameRegex.test(prenom.trim()), [prenom]);
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const birthValid = useMemo(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(birth)) return false;
    const d = new Date(birth);
    if (Number.isNaN(d.getTime())) return false;
    return birth >= "1900-01-01" && birth <= todayIso;
  }, [birth, todayIso]);
  const selectedBirth = useMemo(() => {
    if (!birth) return undefined;
    const parsed = parse(birth, "yyyy-MM-dd", new Date());
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }, [birth]);
  /** 10 chiffres après +229 (saisie identique au NIP). */
  const phoneValid = useMemo(() => /^\d{10}$/.test(numero), [numero]);
  const phoneParts = useMemo(
    () => [numero.slice(0, 2), numero.slice(2, 4), numero.slice(4, 6), numero.slice(6, 8), numero.slice(8, 10)],
    [numero],
  );
  /** NIP patient : exactement 10 chiffres (saisie bloquée). */
  const nipValid = useMemo(() => /^\d{10}$/.test(nip), [nip]);
  const nipParts = useMemo(
    () => [nip.slice(0, 2), nip.slice(2, 4), nip.slice(4, 6), nip.slice(6, 8), nip.slice(8, 10)],
    [nip]
  );
  const departementNom = useMemo(
    () => departements.find((departement) => departement.id === departementId)?.nom ?? "",
    [departements, departementId]
  );
  const adresse = useMemo(
    () =>
      [departementNom, communeNom, arrondissementNom, quartierLibre]
        .map((segment) => segment.trim())
        .filter(Boolean)
        .join("-"),
    [departementNom, communeNom, arrondissementNom, quartierLibre]
  );
  const adresseSlug = useMemo(
    () =>
      [departementNom, communeNom, arrondissementNom, quartierLibre]
        .map((segment) => toSlug(segment))
        .filter(Boolean)
        .join("-"),
    [departementNom, communeNom, arrondissementNom, quartierLibre]
  );

  useEffect(() => {
    let mounted = true;
    getProfessionsMetiers().then((result) => {
      if (!mounted) return;
      setLoadingMetiers(false);
      if (result.ok) setMetiers(result.data);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoadingDepartements(true);
    setAddressError(null);
    getBeninDepartements()
      .then((result) => {
        if (!mounted) return;
        if (!result.ok) {
          setDepartements([]);
          setAddressError(result.message || "Impossible de charger les départements.");
          return;
        }
        setDepartements(result.data);
      })
      .finally(() => {
        if (mounted) setLoadingDepartements(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!departementId) {
      setCommunes([]);
      setCommuneNom("");
      setArrondissements([]);
      setArrondissementNom("");
      return () => {
        mounted = false;
      };
    }
    setLoadingCommunes(true);
    setAddressError(null);
    setCommuneNom("");
    setArrondissements([]);
    setArrondissementNom("");
    getBeninCommunes(departementId)
      .then((result) => {
        if (!mounted) return;
        if (!result.ok) {
          setCommunes([]);
          setAddressError(result.message || "Impossible de charger les communes.");
          return;
        }
        setCommunes(result.data);
      })
      .finally(() => {
        if (mounted) setLoadingCommunes(false);
      });
    return () => {
      mounted = false;
    };
  }, [departementId]);

  useEffect(() => {
    let mounted = true;
    if (!departementId || !communeNom) {
      setArrondissements([]);
      setArrondissementNom("");
      return () => {
        mounted = false;
      };
    }
    setLoadingArrondissements(true);
    setAddressError(null);
    setArrondissementNom("");
    getBeninArrondissements(departementId, communeNom)
      .then((result) => {
        if (!mounted) return;
        if (!result.ok) {
          setArrondissements([]);
          setAddressError(result.message || "Impossible de charger les arrondissements.");
          return;
        }
        setArrondissements(result.data);
      })
      .finally(() => {
        if (mounted) setLoadingArrondissements(false);
      });
    return () => {
      mounted = false;
    };
  }, [departementId, communeNom]);

  const updateNipPart = (index: number, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, "").slice(0, 2);
    const parts = [...nipParts];
    parts[index] = digits;
    setNip(parts.join("").slice(0, 10));
    if (digits.length === 2 && index < 4) {
      requestAnimationFrame(() => nipPartRefs.current[index + 1]?.focus());
    }
  };

  const handleNipPartKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (event.key === "Backspace" && input.value.length === 0 && index > 0) {
      requestAnimationFrame(() => {
        const prev = nipPartRefs.current[index - 1];
        if (!prev) return;
        prev.focus();
        try {
          prev.setSelectionRange(prev.value.length, prev.value.length);
        } catch {
          /* ignore */
        }
      });
    }
  };

  const updatePhonePart = (index: number, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, "").slice(0, 2);
    const parts = [...phoneParts];
    parts[index] = digits;
    setNumero(parts.join("").slice(0, 10));
    if (digits.length === 2 && index < 4) {
      requestAnimationFrame(() => phonePartRefs.current[index + 1]?.focus());
    }
  };

  const handlePhonePartKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (event.key === "Backspace" && input.value.length === 0 && index > 0) {
      requestAnimationFrame(() => {
        const prev = phonePartRefs.current[index - 1];
        if (!prev) return;
        prev.focus();
        try {
          prev.setSelectionRange(prev.value.length, prev.value.length);
        } catch {
          /* ignore */
        }
      });
    }
  };

  const handlePhonePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 10);
    if (!pasted) return;
    event.preventDefault();
    setNumero(pasted);
    const nextIndex = Math.min(4, Math.floor((pasted.length - 1) / 2));
    requestAnimationFrame(() => phonePartRefs.current[nextIndex]?.focus());
  };

  const handleNipPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 10);
    if (!pasted) return;
    event.preventDefault();
    setNip(pasted);
    const nextIndex = Math.min(4, Math.floor((pasted.length - 1) / 2));
    requestAnimationFrame(() => nipPartRefs.current[nextIndex]?.focus());
  };

  /** Une entrée par champ manquant/invalide sur l'étape donnée — un toast est affiché par entrée. */
  const getStepErrors = (targetStep: Step): string[] => {
    const errors: string[] = [];
    if (targetStep === 1) {
      if (!nom.trim()) errors.push("Le nom est obligatoire.");
      else if (!nomValid) errors.push("Le nom contient des caractères non autorisés.");

      if (!prenom.trim()) errors.push("Le(s) prénom(s) sont obligatoires.");
      else if (!prenomValid) errors.push("Le(s) prénom(s) contiennent des caractères non autorisés.");

      if (!birth) errors.push("La date de naissance est obligatoire.");
      else if (!birthValid) errors.push("La date de naissance est invalide.");

      if (!numero) errors.push("Le numéro de téléphone est obligatoire.");
      else if (!phoneValid) errors.push("Le numéro de téléphone doit contenir 10 chiffres.");

      if (!email.trim()) errors.push("L'email est obligatoire.");
      else if (!emailValid) errors.push("L'email n'est pas valide.");

      if (!departementId) errors.push("Le département est obligatoire.");
      if (!communeNom) errors.push("La commune est obligatoire.");
      if (!arrondissementNom) errors.push("L'arrondissement est obligatoire.");

      if (!nip) errors.push("Le NIP du patient est obligatoire.");
      else if (!nipValid) errors.push("Le NIP du patient doit contenir 10 chiffres.");
    }
    return errors;
  };

  /**
   * Crée les vaccinations dans le système réel (VaccinationService, lu par l'onglet
   * "Vaccinations" et la génération du carnet PDF) plutôt que dans l'ancien champ
   * `informationsMedicales.vaccinations` du dossier, qui n'est ni relu ni affiché nulle part.
   * Les erreurs individuelles n'interrompent pas la création du patient/dossier, déjà actée.
   */
  const createVaccinationsForPatient = async (patientNip: string) => {
    for (const v of vaccins) {
      const res = await createVaccinationPro({
        patientNip,
        vaccin: v.nom,
        lot: v.lot.trim() || undefined,
        siteInjection: v.siteInjection.trim() || undefined,
        dateVaccination: new Date().toISOString(),
        professionnelNip: proNip,
        createur: proNip,
      });
      if (!res.ok) {
        setSubmitError((prev) =>
          prev ? `${prev} Vaccination "${v.nom}" non enregistrée : ${res.message}.` : `Vaccination "${v.nom}" non enregistrée : ${res.message}.`
        );
      }
    }
  };

  const onNext = async () => {
    const errors = getStepErrors(step);
    if (errors.length > 0) {
      errors.forEach((message) => showToast(message, "error"));
      return;
    }
    if (step < 3) {
      showToast(`Étape ${step} validée.`, "success");
      setStep((prev) => (prev + 1) as Step);
      return;
    }
    if (!proNip) {
      setSubmitError("Votre session ne contient pas de NIP professionnel. Reconnectez-vous.");
      return;
    }
    if (!birthValid) {
      setSubmitError("Date de naissance invalide.");
      return;
    }
    const dateNaissance = `${birth}T00:00:00.000Z`;
    setSubmitting(true);
    setSubmitError(null);
    const poidsN = poids ? Number(poids) : undefined;
    const tailleN = taille ? Number(taille) : undefined;
    const professionValue = job === "autre" ? jobAutre.trim() : job.trim();
    const patientPayload: Record<string, unknown> = {
      nip: nip.trim(),
      numeroDossier: `DOS-${nip.trim()}`,
      nom: nom.trim().toUpperCase(),
      prenom: prenom.trim(),
      dateNaissance,
      email: email.trim(),
      telephone: `${BJ_DIAL}${numero.trim()}`,
      adresse,
      adresseSlug,
      poids: poidsN,
      taille: tailleN,
      groupeSanguin: gs || undefined,
      electrophorese: electro || undefined,
      profession: professionValue || undefined,
      couvertureAMO: coverage,
      typeAssurance: "AMO",
      createur: proNip,
    };
    const created = await createPatient(patientPayload);
    if (!created.ok) {
      setSubmitError(created.message);
      setSubmitting(false);
      return;
    }
    const data = created.data && typeof created.data === "object" ? (created.data as Record<string, unknown>) : {};
    const patientNip = String(data.nip ?? nip.trim());

    const allergiesList = allergies
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const intolerancesList = intolerances
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const dossierPayload: Record<string, unknown> = {
      patientNip,
      informationsPersonnelles: {
        nom: nom.trim().toUpperCase(),
        prenom: prenom.trim(),
        dateNaissance,
        adresse,
        adresseSlug,
        email: email.trim(),
        telephone: `${BJ_DIAL}${numero.trim()}`,
        profession: professionValue || undefined,
      },
      informationsMedicales: {
        poids: poidsN,
        taille: tailleN,
        groupeSanguin: gs || undefined,
        electrophorese: electro || undefined,
        allergies: allergiesList,
        intolerances: intolerancesList,
        vaccinations: [] as unknown[],
        antecedents: [] as string[],
      },
      createur: proNip,
    };

    const nipOk = /^\d{10}$/.test(patientNip.trim());
    if (!nipOk) {
      setSubmitting(false);
      setSubmitError(
        "Patient enregistré, mais le NIP renvoyé par le serveur est invalide. Ouvrez la liste des patients ou contactez le support.",
      );
      return;
    }

    const dossierRes = await createMedicalRecord(dossierPayload);
    if (!dossierRes.ok) {
      if (dossierRes.errorCode === "ACCES_DOSSIER_REQUIS") {
        setPendingDossier({ patientNip, payload: dossierPayload });
        setSubmitting(false);
        return;
      }
      setSubmitError(
        `Patient créé, mais dossier médical non enregistré : ${dossierRes.message}`
      );
    } else {
      await createVaccinationsForPatient(patientNip);
    }
    setSubmitting(false);
    onCreated(patientNip.trim());
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onNext();
  };

  const handleDossierAccessGranted = async () => {
    if (!pendingDossier) return;
    setSubmitting(true);
    const retry = await createMedicalRecord(pendingDossier.payload);
    if (!retry.ok) {
      setSubmitError(`Dossier médical non enregistré après validation de l'accès : ${retry.message}`);
    } else {
      await createVaccinationsForPatient(pendingDossier.patientNip);
    }
    setSubmitting(false);
    const { patientNip } = pendingDossier;
    setPendingDossier(null);
    onCreated(patientNip);
  };

  return (
    <div>
      {submitError && (
        <div className="mt-4 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 max-w-3xl">
          {submitError}
        </div>
      )}

      <form className="mt-6 space-y-6" onSubmit={onSubmit}>
        {step === 1 && (
          <div className="bg-white rounded-[12px] border p-6" style={{ borderColor: "#E5E7EB" }}>
            <div className="text-2xl font-bold text-[#0B1F16] mb-4">Informations personnelles</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="font-semibold mb-1">
                  Nom
                  <RequiredMark />
                </div>
                <Input
                  value={nom}
                  onChange={(event) => setNom(event.target.value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ' -]/g, "").toUpperCase())}
                  className={nomValid || nom === "" ? "" : "border-red-400"}
                />
              </div>
              <div>
                <div className="font-semibold mb-1">
                  Prenoms
                  <RequiredMark />
                </div>
                <Input
                  value={prenom}
                  onChange={(event) => setPrenom(event.target.value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ' -]/g, "").toUpperCase())}
                  className={prenomValid || prenom === "" ? "" : "border-red-400"}
                />
              </div>
              <div>
                <div className="font-semibold mb-1">
                  Date de naissance
                  <RequiredMark />
                </div>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={`flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                        birthValid || birth === "" ? "border-input" : "border-red-400"
                      }`}
                    >
                      <span className={selectedBirth ? "text-[#0B1F16]" : "text-muted-foreground"}>
                        {selectedBirth ? format(selectedBirth, "d MMMM yyyy", { locale: fr }) : "jj/mm/aaaa"}
                      </span>
                      <CalendarIcon size={16} className="text-gray-500" aria-hidden />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto">
                    <Calendar
                      mode="single"
                      locale={fr}
                      selected={selectedBirth}
                      defaultMonth={selectedBirth ?? new Date(new Date().getFullYear() - 25, 0)}
                      captionLayout="dropdown"
                      startMonth={new Date(new Date().getFullYear() - 120, 0)}
                      endMonth={new Date()}
                      disabled={{ after: new Date() }}
                      onSelect={(date) => {
                        setBirth(date ? format(date, "yyyy-MM-dd") : "");
                        setCalendarOpen(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="md:col-span-2">
                <div className="font-semibold mb-1">
                  Numéro
                  <RequiredMark />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div
                    className="flex h-10 shrink-0 items-center gap-2 rounded-[10px] border bg-[#F3F4F4] px-3"
                    style={{ borderColor: "#E5E7EB" }}
                  >
                    <span className="text-lg leading-none" role="img" aria-label="Bénin">
                      🇧🇯
                    </span>
                    <span className="text-sm font-semibold text-[#0B1F16] tabular-nums">{BJ_DIAL}</span>
                  </div>
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    {phoneParts.map((part, index) => (
                      <div key={`phone-part-${index}`} className="flex items-center gap-2">
                        <Input
                          value={part}
                          ref={(el) => {
                            phonePartRefs.current[index] = el;
                          }}
                          onChange={(e) => updatePhonePart(index, e.target.value)}
                          onKeyDown={(e) => handlePhonePartKeyDown(index, e)}
                          onPaste={handlePhonePaste}
                          inputMode="numeric"
                          autoComplete="tel-national"
                          maxLength={2}
                          placeholder="00"
                          aria-invalid={numero.length > 0 && !phoneValid}
                          aria-label={`Groupe ${index + 1} sur 5`}
                          className={`w-12 text-center ${phoneValid || numero === "" ? "" : "border-red-400"}`}
                        />
                        {index < 4 && (
                          <span className="font-semibold text-[#0B1F16]/70" aria-hidden>
                            -
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <p className="mt-1 text-xs text-[#0B1F16]/60">10 chiffres après {BJ_DIAL} (même format que le NIP).</p>
              </div>
              <div>
                <div className="font-semibold mb-1">
                  Email
                  <RequiredMark />
                </div>
                <Input
                  value={email}
                  onChange={(event) => setEmail(event.target.value.replace(/\s/g, "").toUpperCase())}
                  type="email"
                  placeholder="EXEMPLE@DOMAINE.COM"
                  className={emailValid || email === "" ? "" : "border-red-400"}
                />
              </div>
              <div>
                <div className="font-semibold mb-1">Profession</div>
                <Select
                  value={job || undefined}
                  onValueChange={(value) => {
                    setJob(value);
                    if (value !== "autre") setJobAutre("");
                  }}
                  disabled={loadingMetiers}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loadingMetiers ? "Chargement..." : "Sélectionner une profession"} />
                  </SelectTrigger>
                  <SelectContent>
                    {metiers.map((m) => (
                      <SelectItem key={m.slug} value={m.slug}>{m.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {job === "autre" && (
                  <Input
                    value={jobAutre}
                    onChange={(event) => setJobAutre(event.target.value)}
                    placeholder="Précisez la profession"
                    className="mt-2"
                  />
                )}
              </div>
              <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="font-semibold mb-1">
                    Département
                    <RequiredMark />
                  </div>
                  <Select
                    value={departementId || undefined}
                    onValueChange={setDepartementId}
                    disabled={loadingDepartements}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingDepartements ? "Chargement..." : "Sélectionner un département"} />
                    </SelectTrigger>
                    <SelectContent>
                      {departements.map((departement) => (
                        <SelectItem key={departement.id} value={departement.id}>
                          {departement.nom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="font-semibold mb-1">
                    Commune
                    <RequiredMark />
                  </div>
                  <Select
                    value={communeNom || undefined}
                    onValueChange={setCommuneNom}
                    disabled={!departementId || loadingCommunes}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          !departementId ? "Choisir d'abord un département" : loadingCommunes ? "Chargement..." : "Sélectionner une commune"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {communes.map((commune) => (
                        <SelectItem key={`${commune.id ?? commune.nom}-${commune.nom}`} value={commune.nom}>
                          {commune.nom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="font-semibold mb-1">
                    Arrondissement
                    <RequiredMark />
                  </div>
                  <Select
                    value={arrondissementNom || undefined}
                    onValueChange={setArrondissementNom}
                    disabled={!communeNom || loadingArrondissements}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          !communeNom
                            ? "Choisir d'abord une commune"
                            : loadingArrondissements
                              ? "Chargement..."
                              : "Sélectionner un arrondissement"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {arrondissements.map((arrondissement) => (
                        <SelectItem
                          key={`${arrondissement.id ?? arrondissement.nom}-${arrondissement.nom}`}
                          value={arrondissement.nom}
                        >
                          {arrondissement.nom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="font-semibold mb-1">Quartier</div>
                  <Input
                    value={quartierLibre}
                    onChange={(event) => setQuartierLibre(event.target.value.toUpperCase())}
                    placeholder="EX: HOUEYIHO"
                  />
                </div>
                <div className="md:col-span-2">
                  <div className="font-semibold mb-1">Adresse (concaténée)</div>
                  <Input
                    value={adresse}
                    readOnly
                    className="bg-[#EEF1F1]"
                    placeholder="Departement-Commune-Arrondissement-Quartier"
                  />
                  {addressError && <p className="mt-1 text-xs text-red-600">{addressError}</p>}
                </div>
              </div>
              <div>
                <div className="font-semibold mb-1">
                  NIP
                  <RequiredMark />
                </div>
                <div className="flex items-center gap-2">
                  {nipParts.map((part, index) => (
                    <div key={`nip-part-${index}`} className="flex items-center gap-2">
                      <Input
                        value={part}
                        ref={(element) => {
                          nipPartRefs.current[index] = element;
                        }}
                        onChange={(event) => updateNipPart(index, event.target.value)}
                        onKeyDown={(event) => handleNipPartKeyDown(index, event)}
                        onPaste={handleNipPaste}
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={2}
                        placeholder="00"
                        aria-invalid={nip.length > 0 && !nipValid}
                        className={`w-12 text-center ${nipValid || nip === "" ? "" : "border-red-400"}`}
                      />
                      {index < 4 && <span className="text-[#0B1F16]/70 font-semibold">-</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white rounded-[12px] border p-6" style={{ borderColor: "#E5E7EB" }}>
            <div className="text-2xl font-bold text-[#0B1F16] mb-4">Informations medicales</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="font-semibold mb-1">Taille</div>
                <div className="relative">
                  <Input
                    value={taille}
                    onChange={(event) => applyDigitsWithCaret(event, setTaille, tailleRef, 4)}
                    ref={tailleRef}
                    inputMode="numeric"
                    pattern="\d*"
                    className="pr-10"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">cm</span>
                </div>
              </div>
              <div>
                <div className="font-semibold mb-1">Groupe sanguin</div>
                <Select value={gs || undefined} onValueChange={setGs}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {GROUPES_SANGUINS.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="font-semibold mb-1">Poids</div>
                <div className="relative">
                  <Input
                    value={poids}
                    onChange={(event) => applyDigitsWithCaret(event, setPoids, poidsRef, 4)}
                    ref={poidsRef}
                    inputMode="numeric"
                    pattern="\d*"
                    className="pr-10"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kg</span>
                </div>
              </div>
              <div>
                <div className="font-semibold mb-1">Electrophorèse</div>
                <Select value={electro || undefined} onValueChange={setElectro}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {ELECTROPHORESES.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="font-semibold mb-1">Allergies</div>
                <Input value={allergies} onChange={(event) => setAllergies(event.target.value.toUpperCase())} placeholder="EX : PÉNICILLINE, ARACHIDE…" />
              </div>
              <div>
                <div className="font-semibold mb-1">Intolérances</div>
                <Input value={intolerances} onChange={(event) => setIntolerances(event.target.value.toUpperCase())} placeholder="EX : LACTOSE, GLUTEN…" />
              </div>
              <div className="md:col-span-2">
                <div className="font-semibold mb-2">Vaccinations</div>
                <div className="space-y-2 relative">
                  {vaccins.map((vaccine) => (
                    <div
                      key={vaccine.nom}
                      className="flex flex-wrap items-center gap-2 rounded-[10px] border bg-white p-2"
                      style={{ borderColor: "#E5E7EB" }}
                    >
                      <span className="inline-flex items-center gap-2 px-2 h-9 font-medium">
                        {vaccine.nom}
                        <button
                          type="button"
                          onClick={() => setVaccins((values) => values.filter((v) => v.nom !== vaccine.nom))}
                          aria-label={`Retirer ${vaccine.nom}`}
                        >
                          ✕
                        </button>
                      </span>
                      <Input
                        value={vaccine.lot}
                        onChange={(event) =>
                          setVaccins((values) =>
                            values.map((v) => (v.nom === vaccine.nom ? { ...v, lot: event.target.value } : v))
                          )
                        }
                        placeholder="N° Lot"
                        className="h-9 w-32"
                      />
                      <Input
                        value={vaccine.siteInjection}
                        onChange={(event) =>
                          setVaccins((values) =>
                            values.map((v) => (v.nom === vaccine.nom ? { ...v, siteInjection: event.target.value } : v))
                          )
                        }
                        placeholder="Site injection"
                        className="h-9 w-36"
                      />
                    </div>
                  ))}
                  <div className="relative">
                    <button
                      type="button"
                      className="inline-flex items-center px-3 h-9 rounded-[10px] border bg-white"
                      style={{ borderColor: "#E5E7EB" }}
                      onClick={() => setPickerOpen((open) => !open)}
                    >
                      Ajouter
                    </button>
                    {pickerOpen && (
                      <div className="absolute z-10 mt-2 w-56 bg-white rounded-[10px] border p-3 space-y-2 shadow" style={{ borderColor: "#E5E7EB" }}>
                        {["Varicelle", "BCG", "DTP", "MMR", "Hépatite", "HPV", "Hib"].map((option) => {
                          const checked = vaccins.some((v) => v.nom === option);
                          return (
                            <label key={option} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(event) => {
                                  setVaccins((values) =>
                                    event.target.checked
                                      ? [...values, { nom: option, lot: "", siteInjection: "" }]
                                      : values.filter((v) => v.nom !== option)
                                  );
                                }}
                              />
                              {option}
                            </label>
                          );
                        })}
                        <div className="pt-2 flex justify-end">
                          <button className="h-9 px-3 rounded-[10px] font-semibold text-white" style={{ background: "#00553E" }} onClick={() => setPickerOpen(false)}>
                            Ajouter
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="bg-white rounded-[12px] border p-6" style={{ borderColor: "#E5E7EB" }}>
            <div className="text-2xl font-bold text-[#0B1F16] mb-4">Assurance</div>
            <div>
              <div className="font-semibold mb-3">Couverture AMO</div>
              <div className="flex flex-wrap gap-3">
                {([
                  { value: "oui", label: "Assuré (AMO actif)", desc: "Patient couvert par l'AMO" },
                  { value: "non", label: "Non assuré", desc: "Pas de couverture AMO" },
                  { value: "verification", label: "À vérifier", desc: "Statut à confirmer" },
                ] as const).map(({ value, label, desc }) => (
                  <label
                    key={value}
                    className={`flex items-start gap-3 cursor-pointer rounded-[10px] border px-4 py-3 flex-1 min-w-[160px] transition-colors ${
                      coverage === value ? "border-[#00553E] bg-[#00553E]/5" : "border-[#E5E7EB] bg-[#F3F4F4]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="coverage"
                      value={value}
                      checked={coverage === value}
                      onChange={() => setCoverage(value)}
                      className="mt-0.5 accent-[#00553E]"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-[#0B1F16]">{label}</span>
                      <span className="block text-xs text-gray-500 mt-0.5">{desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <div className="font-semibold mb-2">Soins inclus</div>
              <div className="flex flex-wrap gap-2">
                {soins.map((soin) => (
                  <span key={soin} className="inline-flex items-center gap-2 px-3 h-9 rounded-[10px] border bg-white" style={{ borderColor: "#E5E7EB" }}>
                    {soin}
                    <button type="button" onClick={() => setSoins((values) => values.filter((value) => value !== soin))}>
                      ✕
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => { setSoinDraft(""); setSoinModalOpen(true); requestAnimationFrame(() => soinInputRef.current?.focus()); }}
                  className="h-9 px-3 rounded-[10px] border"
                  style={{ borderColor: "#00553E", color: "#00553E" }}
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-0 md:justify-between">
          <div className="flex items-center justify-center md:justify-start gap-4 md:gap-6">
            <div className={`h-1.5 w-16 md:w-24 rounded-full ${step >= 1 ? "bg-[#00553E]" : "bg-gray-300"}`} />
            <div className={`h-1.5 w-16 md:w-24 rounded-full ${step >= 2 ? "bg-[#00553E]" : "bg-gray-300"}`} />
            <div className={`h-1.5 w-16 md:w-24 rounded-full ${step >= 3 ? "bg-[#00553E]" : "bg-gray-300"}`} />
          </div>
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 w-full md:w-auto">
            {step > 1 && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => {
                  setSubmitError(null);
                  setStep((prev) => (prev - 1) as Step);
                }}
                disabled={submitting}
                className="border-[#00553E] bg-transparent text-[#00553E] hover:bg-[#00553E]/5 hover:text-[#00553E] w-full md:w-auto"
              >
                ⟵ Précédent
              </Button>
            )}
            <Button
              type="submit"
              size="lg"
              disabled={submitting}
              className="bg-[#00553E] text-white hover:bg-[#00553E]/90 w-full md:w-auto"
            >
              {submitting ? "Enregistrement…" : step === 3 ? "Valider" : "Suivant ➜"}
            </Button>
          </div>
        </div>
      </form>

      {soinModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setSoinModalOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-[12px] bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 text-lg font-bold text-[#0B1F16]">Ajouter un soin</div>
            <Input
              ref={soinInputRef}
              value={soinDraft}
              onChange={(e) => setSoinDraft(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const v = soinDraft.trim();
                  if (v) { setSoins((prev) => [...prev, v]); setSoinModalOpen(false); }
                }
                if (e.key === "Escape") setSoinModalOpen(false);
              }}
              placeholder="EX : RADIOLOGIE, KINÉSITHÉRAPIE…"
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setSoinModalOpen(false)}
                className="h-9 px-4 rounded-[10px] border text-sm font-medium text-[#0B1F16]"
                style={{ borderColor: "#E5E7EB" }}
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={!soinDraft.trim()}
                onClick={() => {
                  const v = soinDraft.trim();
                  if (v) { setSoins((prev) => [...prev, v]); setSoinModalOpen(false); }
                }}
                className="h-9 px-4 rounded-[10px] text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: "#00553E" }}
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      <PatientAccessOtpModal
        open={pendingDossier !== null}
        patientNip={pendingDossier?.patientNip ?? ""}
        patientDisplayName={[prenom, nom].filter(Boolean).join(" ") || undefined}
        onClose={() => {
          const target = pendingDossier?.patientNip;
          setPendingDossier(null);
          if (target) onCreated(target);
        }}
        onSuccess={() => void handleDossierAccessGranted()}
      />
    </div>
  );
}

export default function AddPatient() {
  const navigate = useNavigate();
  return (
    <div>
      <h1 className="text-3xl md:text-4xl font-bold text-[#0B1F16]">Gestion des patients</h1>
      <div className="mt-4 text-sm" style={{ color: "#00553E" }}>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2">
          <ArrowLeft size={16} /> Retour
        </button>
      </div>
      <div className="mt-2">
        <div className="text-2xl font-semibold text-[#0B1F16]">Ajouter un patient</div>
      </div>
      <PatientCreationWizard
        onCreated={(patientNip) => navigate(`/pro/dashboard/patients/${encodeURIComponent(patientNip)}`)}
      />
    </div>
  );
}

